/**
 * Configuración de países desde la base de datos.
 * Única fuente: tabla countries_config. No hay configuración estática.
 */

import { supabase } from "@/shared/db/supabase-client";

export type CountryCode = "MX" | "RD" | "CO" | "CL" | "PE" | "US";

export type Region =
  | "North America"
  | "Central America"
  | "South America"
  | "Caribbean";

export interface CountryConfig {
  code: CountryCode;
  nameEs: string;
  nameEn: string;
  flag: string;
  enabled: boolean;
  region: Region;
  locale: string;
  timezone: string;
  dateFormat: string;
  primaryLanguage: string;
  currencyCode: string;
  currencyNameEs: string;
  currencyNameEn: string;
  legalSystemEs: string;
  legalSystemEn: string;
  legalSystemYear?: number | null;
  isoCodeLower: string;
}

interface CountryConfigRow {
  code: string;
  name_es: string;
  name_en: string;
  flag: string;
  enabled: boolean;
  region: string;
  locale: string;
  timezone: string;
  date_format: string;
  primary_language: string;
  currency_code: string;
  currency_name_es: string;
  currency_name_en: string;
  legal_system_es: string;
  legal_system_en: string;
  legal_system_year: number | null;
  iso_code_lower: string;
}

function mapRow(row: CountryConfigRow): CountryConfig {
  return {
    code: row.code as CountryCode,
    nameEs: row.name_es,
    nameEn: row.name_en,
    flag: row.flag,
    enabled: row.enabled,
    region: row.region as Region,
    locale: row.locale,
    timezone: row.timezone,
    dateFormat: row.date_format,
    primaryLanguage: row.primary_language,
    currencyCode: row.currency_code,
    currencyNameEs: row.currency_name_es,
    currencyNameEn: row.currency_name_en,
    legalSystemEs: row.legal_system_es,
    legalSystemEn: row.legal_system_en,
    legalSystemYear: row.legal_system_year,
    isoCodeLower: row.iso_code_lower,
  };
}

export async function getCountries(): Promise<CountryConfig[]> {
  const { data, error } = await supabase
    .from("countries_config")
    .select("*")
    .order("name_es");

  if (error) {
    console.error("Error fetching countries:", error);
    return [];
  }

  return (data || []).map(mapRow);
}

export async function getCountry(code: string): Promise<CountryConfig | null> {
  const { data, error } = await supabase
    .from("countries_config")
    .select("*")
    .eq("code", code.toUpperCase())
    .single();

  if (error || !data) return null;
  return mapRow(data as CountryConfigRow);
}

export async function getCountryCodes(): Promise<CountryCode[]> {
  const countries = await getCountries();
  return countries.map((c) => c.code);
}

export async function getEnabledCountries(): Promise<CountryConfig[]> {
  const { data, error } = await supabase
    .from("countries_config")
    .select("*")
    .eq("enabled", true)
    .order("name_es");

  if (error) {
    console.error("Error fetching enabled countries:", error);
    return [];
  }

  return (data || []).map(mapRow);
}

export async function isValidCountryCode(code: string): Promise<boolean> {
  const country = await getCountry(code);
  return country !== null;
}

export async function isCountryEnabled(code: string): Promise<boolean> {
  const country = await getCountry(code);
  return country?.enabled === true;
}

export async function getCountriesByRegion(
  region: Region
): Promise<CountryConfig[]> {
  const { data, error } = await supabase
    .from("countries_config")
    .select("*")
    .eq("region", region)
    .order("name_es");

  if (error) return [];
  return (data || []).map(mapRow);
}

export async function getCountryCodeByName(
  name: string
): Promise<CountryCode | null> {
  const n = name.toLowerCase().trim();
  const { data } = await supabase
    .from("countries_config")
    .select("code")
    .or(`name_es.ilike.%${n}%,name_en.ilike.%${n}%`)
    .limit(1)
    .single();

  return data?.code as CountryCode | null;
}

/** Fallback síncrono para document.schema (zod requiere lista al cargar). Reflejar países en BD. */
export function getCountryCodesSync(): CountryCode[] {
  return ["RD", "MX", "CO", "CL", "PE", "US"];
}
